import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import { getCarrierDisplayName } from 'helpers/appointments/appointments';
import { useSelector } from 'react-redux';
import { cn } from 'lib/utils';
import Separator from 'tailwind-components/Separator/Separator';
import SidebarContent from 'tailwind-components/ContentWithSidebar/SidebarContent';
import { getStateNameFromStateCode } from 'helpers/states';
import { getCompany } from 'helpers/getCompanyVariables';
import {
  CARRIER_STATE_DISPLAY_STATUS,
  NO_CARRIERS_MATCH_FILTERS_MESSAGE,
  getCarrierCategoryLabel,
  getCarrierStateDisplayStatus
} from 'helpers/carriers/carriers';
import { usePortalWalkthrough } from 'tailwind-components/PortalWalkthrough/PortalWalkthroughContext';

const company = getCompany();

const isOneill = company === 'oneill';

const BUTTON_BASE =
  'w-full cursor-pointer rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors duration-200 ease-in-out text-center';

const VARIANT_STYLES = {
  actionable:
    'border-semantic-danger bg-semantic-danger/40 text-gray-900 hover:bg-semantic-danger/50',
  appointed:
    'border-secondary bg-secondary-light text-gray-900 hover:bg-secondary/50',
  pending: 'border-pending bg-pending/50 text-gray-900 hover:bg-pending/60',
  'offered-not-available':
    'border-slate-300 bg-slate-100 text-gray-800 hover:bg-slate-200',
  default: 'border-gray-200 bg-white text-gray-900 hover:bg-gray-100'
};

function carrierListButtonClass(variant) {
  return cn(BUTTON_BASE, VARIANT_STYLES[variant] ?? VARIANT_STYLES.default);
}

const sortNames = list => [...list].sort((a, b) => a.localeCompare(b));

const MOCK_STATUSES = [
  'appointed',
  'pending',
  'action-needed',
  'not-appointed',
  'not-appointed'
];

export default function ByStateCarriersSidebar({
  carrierNames = [],
  onCarrierClick,
  mapSelectedStateCode,
  hasActiveFilters = false
}) {
  const tourCtx = usePortalWalkthrough();
  const isTourActive = tourCtx?.isActive ?? false;

  const user = useSelector(state => state.user.zohoData);
  const { actionableAppointments, requestedAppointments, carriersArray } =
    useSelector(state => state.appointments);

  const mockSections = useMemo(() => {
    const pool = carriersArray.slice(0, 5);
    const grouped = {
      appointed: [],
      pending: [],
      'action-needed': [],
      'not-appointed': []
    };
    pool.forEach((c, i) => {
      const key = MOCK_STATUSES[i] ?? 'not-appointed';
      grouped[key].push(c.name);
    });
    return [
      {
        id: 'appointed',
        title: isOneill ? 'Confirmed' : 'Active',
        variant: 'appointed',
        names: grouped.appointed
      },
      {
        id: 'pending',
        title: 'Pending',
        variant: 'pending',
        names: grouped.pending
      },
      {
        id: 'action-needed',
        title: 'Pending Agent Action',
        variant: 'actionable',
        names: grouped['action-needed']
      },
      {
        id: 'not-appointed',
        title: 'Not Requested',
        variant: 'default',
        names: grouped['not-appointed']
      }
    ].filter(s => s.names.length > 0);
  }, [carriersArray]);

  const stateName = getStateNameFromStateCode(mapSelectedStateCode);

  const categoryByName = useMemo(
    () =>
      Object.fromEntries(
        carriersArray.map(c => [c.name, getCarrierCategoryLabel(c)])
      ),
    [carriersArray]
  );

  const sections = useMemo(() => {
    const actionNeeded = [];
    const appointed = [];
    const pendingRequest = [];
    const offeredNotAvailable = [];
    const notAppointed = [];

    for (const name of carrierNames) {
      const carrierData = carriersArray.find(c => c.name === name);

      const status = carrierData
        ? getCarrierStateDisplayStatus(
            carrierData,
            stateName,
            user,
            requestedAppointments,
            actionableAppointments
          )
        : CARRIER_STATE_DISPLAY_STATUS.NOT_APPOINTED;

      if (status === CARRIER_STATE_DISPLAY_STATUS.PENDING_AGENT_ACTION) {
        actionNeeded.push(name);
        continue;
      }
      if (status === CARRIER_STATE_DISPLAY_STATUS.CONFIRMED) {
        appointed.push(name);
        continue;
      }
      if (status === CARRIER_STATE_DISPLAY_STATUS.PENDING) {
        pendingRequest.push(name);
        continue;
      }
      if (status === CARRIER_STATE_DISPLAY_STATUS.OFFERED_NOT_AVAILABLE) {
        offeredNotAvailable.push(name);
        continue;
      }
      notAppointed.push(name);
    }

    return [
      {
        id: 'appointed',
        title: isOneill ? 'Confirmed' : 'Active',
        variant: 'appointed',
        names: sortNames(appointed)
      },
      {
        id: 'pending',
        title: 'Pending',
        variant: 'pending',
        names: sortNames(pendingRequest)
      },
      {
        id: 'action-needed',
        title: 'Pending Agent Action',
        variant: 'actionable',
        names: sortNames(actionNeeded)
      },
      {
        id: 'offered-not-available',
        title: 'Offered (Not available)',
        variant: 'offered-not-available',
        names: sortNames(offeredNotAvailable)
      },
      {
        id: 'not-appointed',
        title: 'Not Requested',
        variant: 'default',
        names: sortNames(notAppointed)
      }
    ];
  }, [
    actionableAppointments,
    carrierNames,
    carriersArray,
    mapSelectedStateCode,
    requestedAppointments,
    stateName,
    user
  ]);

  const visibleSections = sections.filter(s => s.names.length > 0);

  const sectionsToRender = isTourActive ? mockSections : visibleSections;

  if (carrierNames.length === 0 && !isTourActive) {
    return (
      <SidebarContent>
        <p className='m-0 text-base text-gray-500 text-center'>
          {hasActiveFilters
            ? NO_CARRIERS_MATCH_FILTERS_MESSAGE
            : 'No carriers found for this state.'}
        </p>
      </SidebarContent>
    );
  }

  return (
    <SidebarContent
      showScrollbar
      includesHeader
      dataTour='carrier-appt-by-state-sidebar'
    >
      <p className='m-0 text-base text-gray-600'>
        Please click on a carrier icon below to view more information
      </p>
      {sectionsToRender.map((section, index) => (
        <section key={section.id} className='flex flex-col gap-2'>
          {index > 0 ? <Separator variant='dashed' className='my-1' /> : null}
          <h3 className='m-0 text-xs font-semibold uppercase tracking-wide text-gray-500'>
            {section.title}
          </h3>
          <ul className='m-0 flex list-none flex-col gap-2 p-0'>
            {section.names.map(name => (
              <li key={name}>
                <button
                  type='button'
                  className={carrierListButtonClass(section.variant)}
                  onClick={() => onCarrierClick(name)}
                >
                  {getCarrierDisplayName(name)}
                  {categoryByName[name] && (
                    <span className='mt-0.5 block text-xs font-normal text-gray-500'>
                      {categoryByName[name]}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </SidebarContent>
  );
}

ByStateCarriersSidebar.propTypes = {
  carrierNames: propTypes.arrayOf(propTypes.string),
  onCarrierClick: propTypes.func.isRequired,
  mapSelectedStateCode: propTypes.string,
  hasActiveFilters: propTypes.bool
};
